import React, { createContext, useContext } from "react";
import { useFeatureFlag } from "@jsq/flags-react";

/**
 * Release gate for the package-detail Activity tab, shaped like the fay auth
 * gate: the provider reads the raw flag once and republishes it through a plain
 * context.
 *
 * Consumers read the context rather than the raw hook because `useFeatureFlag`
 * throws with no FlagProvider above it — reading the derived value keeps a
 * component rendered on its own out of that requirement in its tests. The
 * context defaults to false, so a host that never mounts the provider simply
 * doesn't get the tab.
 */
const ActivityTabEnabledContext = createContext<boolean>(false);

/**
 * Raw read of the `fayActivityTracking` release flag, keeping the key and
 * default in one place (per the `@jsq/flags-react` wrapping-hook guidance).
 *
 * Only the provider below calls this, and it always renders under a real
 * FlagProvider. Everything else reads `useActivityTabEnabled`.
 */
export function useActivityTabFlag(): boolean {
  return useFeatureFlag("fayActivityTracking", false);
}

type Props = {
  children: React.ReactNode;
};

export const ActivityTabFlagProvider: React.FC<Props> = ({ children }) => {
  const enabled = useActivityTabFlag();
  return (
    <ActivityTabEnabledContext.Provider value={enabled}>
      {children}
    </ActivityTabEnabledContext.Provider>
  );
};

/** True only when an `ActivityTabFlagProvider` is above us AND the release flag
 *  is on. False outside the provider, so the tab stays hidden rather than a
 *  missing FlagProvider throwing. */
export function useActivityTabEnabled(): boolean {
  return useContext(ActivityTabEnabledContext);
}
